import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Lock, Search, X } from 'lucide-react'; 

import JobPostingPreview from '../Recruiter/JobPosting/JobPostingPreview';
import './CandidateJobs.css'; 

// --- THE ELIGIBILITY CHECKER ---
const checkEligibility = (candidateProfile, job) => {
    if (!candidateProfile || !job) return { isEligible: false, reasons: ['Missing profile or job data.'] };
    const reasons = [];
    
    const reqCGPA = parseFloat(job.minCGPA);
    const candCGPA = parseFloat(candidateProfile.currentCGPA);
    if (!isNaN(reqCGPA) && !isNaN(candCGPA) && candCGPA < reqCGPA) reasons.push(`Min CGPA ${reqCGPA} required (You have ${candCGPA}).`);
    
    const req10th = parseFloat(job.tenth);
    const cand10th = parseFloat(candidateProfile.tenthPercentile);
    if (!isNaN(req10th) && !isNaN(cand10th) && cand10th < req10th) reasons.push(`Min 10th score ${req10th}% required.`);
    
    const req12th = parseFloat(job.twelfth);
    const cand12th = parseFloat(candidateProfile.twelfthPercentile);
    if (!isNaN(req12th) && !isNaN(cand12th) && cand12th < req12th) reasons.push(`Min 12th score ${req12th}% required.`);
    
    if (job.passoutYear && candidateProfile.passoutYear && job.passoutYear !== candidateProfile.passoutYear) reasons.push(`Requires passout year ${job.passoutYear}.`);
    if (job.gender && job.gender !== 'Any' && candidateProfile.gender && job.gender !== candidateProfile.gender) reasons.push(`Requires gender: ${job.gender}.`);
    
    return { isEligible: reasons.length === 0, reasons };
};

const CandidateJobs = ({ userId, setPremiumCredits, onGoToPayments }) => {
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState('Open');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Custom Modal States
  const [selectedJob, setSelectedJob] = useState(null);
  
  const [candidateProfile, setCandidateProfile] = useState(null);
  const [eligibilityStatus, setEligibilityStatus] = useState({ isEligible: true, reasons: [] }); 
  const [appliedJobIds, setAppliedJobIds] = useState(new Set()); 

  const [activeTab, setActiveTab] = useState('Local');

  // 🚨 THE FIX: Moved getJobStatus up so we can use it to calculate the exact badge number
  const getJobStatus = (applyByDate, applyByTime) => {
    if (!applyByDate) return 'Open';
    try {
        const applyDateTime = new Date(`${applyByDate}T${applyByTime || '23:59:59'}`);
        return new Date() < applyDateTime ? 'Open' : 'Closed';
    } catch { return 'Open'; }
  };

  const premiumCredits = jobs.length > 0 ? jobs[0].userPremiumCredits : 0;
  const localJobs = jobs.filter(job => !job.isPremiumJob);
  const internationalJobs = jobs.filter(job => job.isPremiumJob);
  const displayedJobsList = activeTab === 'Local' ? localJobs : internationalJobs;

  // 🚨 THE FIX: Calculates the EXACT number of currently open premium jobs
  const livePremiumCount = internationalJobs.filter(job => getJobStatus(job.applyByDate, job.applyByTime) === 'Open').length;

  const formatApplyBy = (applyByDate, applyByTime) => {
    if (!applyByDate) return 'N/A';
    try {
      return `${new Date(applyByDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}${applyByTime ? ` by ${applyByTime}` : ''}`;
    } catch { return 'Invalid Date'; }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!userId) return setIsLoading(false);
      setIsLoading(true);
      try {
        const [jobsRes, profileRes, appliedRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/candidate/posted-jobs/${userId}`),
          axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/candidate/profile/${userId}`),
          axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/applications/my/${userId}`)
        ]);
        setJobs(jobsRes.data);
        setCandidateProfile(profileRes.data);
        setAppliedJobIds(new Set(appliedRes.data));
        
        if (setPremiumCredits) {
          setPremiumCredits(jobsRes.data.length > 0 ? jobsRes.data[0].userPremiumCredits : 0);
        }
      } catch (error) {
        console.error('Failed to fetch:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [userId, setPremiumCredits]);

  const openModal = (job) => {
    if (!candidateProfile) return alert("Profile loading. Please wait.");
    setEligibilityStatus(checkEligibility(candidateProfile, job));
    setSelectedJob(job); // Triggers our custom modal!
  };

  const closeModal = () => {
    setSelectedJob(null);
  };

  const handleApplyNow = async (jobId) => {
      if (!window.confirm("Are you sure you want to apply?")) return;
      try {
        const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/applications/${jobId}`, { userId });
        alert(response.data.message || 'Applied Successfully!');
        setAppliedJobIds(prev => new Set(prev).add(jobId)); 
        
        if (selectedJob.isPremiumJob) {
           const newCredits = premiumCredits - 1;
           setJobs(prev => prev.map(j => ({ ...j, userPremiumCredits: newCredits })));
           if (setPremiumCredits) setPremiumCredits(newCredits); 
        }
        closeModal();
      } catch (error) {
        alert(`Could not apply: ${error.response?.data?.message || 'Server error.'}`);
      }
  };

  const filteredJobs = displayedJobsList
    .filter(job => getJobStatus(job.applyByDate, job.applyByTime) === filter)
    .filter(job =>
      (job.companyName && job.companyName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (job.role && job.role.toLowerCase().includes(searchTerm.toLowerCase()))
    );

  if (isLoading) return <div className="loading-message">Loading available jobs...</div>;

  const isLocked = selectedJob?.isPremiumJob && premiumCredits <= 0;

  return (
    <div className="candidate-jobs-container" style={{ padding: '0' }}>
      
      {/* Search and Filters Header */}
      <div style={{ 
        display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', 
        background: '#fff', padding: '12px 20px', borderRadius: '12px', 
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)', marginBottom: '20px', gap: '15px'
      }}>
        <div style={{ display: 'flex', gap: '5px', background: '#f1f5f9', padding: '4px', borderRadius: '8px' }}>
           <button 
              onClick={() => setActiveTab('Local')}
              style={{ padding: '8px 20px', fontSize: '0.95rem', fontWeight: '600', cursor: 'pointer', background: activeTab === 'Local' ? '#fff' : 'transparent', color: activeTab === 'Local' ? '#0f172a' : '#64748b', border: 'none', borderRadius: '6px', transition: 'all 0.2s', boxShadow: activeTab === 'Local' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}
           >
              Local Jobs
           </button>
           <button 
              onClick={() => setActiveTab('International')}
              style={{ padding: '8px 20px', fontSize: '0.95rem', fontWeight: '600', cursor: 'pointer', background: activeTab === 'International' ? '#fff' : 'transparent', color: activeTab === 'International' ? '#c026d3' : '#64748b', border: 'none', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s', boxShadow: activeTab === 'International' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}
           >
              Global Premium
              {livePremiumCount > 0 && (
                <span style={{ background: '#ef4444', color: '#fff', padding: '2px 6px', borderRadius: '10px', fontSize: '0.7rem', animation: 'pulse 2s infinite' }}>
                  {livePremiumCount} Live
                </span>
              )}
           </button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <div style={{ display: 'flex', background: '#f1f5f9', padding: '4px', borderRadius: '8px' }}>
            <button onClick={() => setFilter('Open')} style={{ padding: '6px 16px', fontSize: '0.85rem', fontWeight: '600', background: filter === 'Open' ? '#2563eb' : 'transparent', color: filter === 'Open' ? '#fff' : '#64748b', border: 'none', borderRadius: '6px', cursor: 'pointer', transition: 'all 0.2s' }}>Open</button>
            <button onClick={() => setFilter('Closed')} style={{ padding: '6px 16px', fontSize: '0.85rem', fontWeight: '600', background: filter === 'Closed' ? '#2563eb' : 'transparent', color: filter === 'Closed' ? '#fff' : '#64748b', border: 'none', borderRadius: '6px', cursor: 'pointer', transition: 'all 0.2s' }}>Closed</button>
          </div>
          <div style={{ position: 'relative' }}>
            <Search size={16} color="#94a3b8" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
            <input 
              type="text" placeholder="Search role..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} 
              style={{ padding: '8px 16px 8px 35px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.9rem', outline: 'none', width: '200px' }} 
            />
          </div>
        </div>
      </div>

      {/* Jobs Table */}
      <div className="jobs-table-wrapper" style={{ background: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
        <table className="jobs-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
            <tr>
              <th style={{ padding: '15px 20px', textAlign: 'left', fontSize: '0.85rem', color: '#64748b', textTransform: 'uppercase' }}>Company</th>
              <th style={{ padding: '15px 20px', textAlign: 'left', fontSize: '0.85rem', color: '#64748b', textTransform: 'uppercase' }}>Title</th>
              <th style={{ padding: '15px 20px', textAlign: 'left', fontSize: '0.85rem', color: '#64748b', textTransform: 'uppercase' }}>Apply By</th>
              <th style={{ padding: '15px 20px', textAlign: 'left', fontSize: '0.85rem', color: '#64748b', textTransform: 'uppercase' }}>Type</th>
              <th style={{ padding: '15px 20px', textAlign: 'left', fontSize: '0.85rem', color: '#64748b', textTransform: 'uppercase' }}>Location</th>
              <th style={{ padding: '15px 20px', textAlign: 'left', fontSize: '0.85rem', color: '#64748b', textTransform: 'uppercase' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredJobs.length > 0 ? (
              filteredJobs.map((job) => (
                  <tr key={job.id || job._id} onClick={() => openModal(job)} className="clickable-row" style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer', transition: 'background 0.2s' }}>
                    <td style={{ padding: '15px 20px' }}><strong style={{ color: job.isPremiumJob ? '#c026d3' : '#0f172a' }}>{job.companyName}</strong></td>
                    <td style={{ padding: '15px 20px', color: '#475569' }}>{job.role}</td>
                    <td style={{ padding: '15px 20px', color: '#475569', fontSize: '0.9rem' }}>{formatApplyBy(job.applyByDate, job.applyByTime)}</td>
                    <td style={{ padding: '15px 20px' }}>
                      <span style={{ background: '#f1f5f9', color: '#475569', padding: '4px 8px', borderRadius: '6px', fontSize: '0.8rem' }}>{job.typeOfWork}</span>
                    </td>
                    <td style={{ padding: '15px 20px', color: '#64748b', fontSize: '0.9rem' }}>{job.location || 'N/A'}</td>
                    <td style={{ padding: '15px 20px' }}>
                      <span className={`status status-${getJobStatus(job.applyByDate, job.applyByTime).toLowerCase()}`}>{getJobStatus(job.applyByDate, job.applyByTime)}</span>
                    </td>
                  </tr>
              ))
            ) : (
               <tr><td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>No {filter} {activeTab} jobs found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ============================================================== */}
      {/* 🚀 NATIVE BULLETPROOF CUSTOM MODAL (NO MORE REACT-MODAL BUGS) 🚀 */}
      {/* ============================================================== */}
      {selectedJob && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(5px)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px' }} onClick={closeModal}>
          <div style={{ backgroundColor: '#fff', width: '100%', maxWidth: '800px', maxHeight: '90vh', borderRadius: '16px', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)' }} onClick={(e) => e.stopPropagation()}>
            
            {/* Modal Header */}
            <div style={{ padding: '20px 32px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h2 style={{ margin: 0, color: '#0f172a', fontSize: '1.5rem' }}>{isLocked ? "Premium Job Locked" : "Job Details"}</h2>
                <p style={{ margin: '4px 0 0', color: '#64748b' }}>{selectedJob.companyName}</p>
              </div>
              <button onClick={closeModal} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}>
                <X size={24} />
              </button>
            </div>
            
            {/* Modal Body */}
            <div style={{ overflowY: 'auto', flex: 1, padding: '20px', position: 'relative' }}>
              {isLocked ? (
                <>
                  <div style={{ filter: 'blur(8px)', pointerEvents: 'none', userSelect: 'none', opacity: 0.4 }}>
                    <JobPostingPreview formData={selectedJob} />
                  </div>
                  <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', background: '#fff', padding: '40px', borderRadius: '16px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', border: '2px solid #f59e0b', width: '85%' }}>
                    <Lock size={56} color="#f59e0b" style={{ margin: '0 auto 20px auto' }} />
                    <h3 style={{ margin: '0 0 15px 0', color: '#0f172a', fontSize: '1.5rem' }}>Global Opportunity Locked</h3>
                    <p style={{ color: '#475569', fontSize: '1.05rem', lineHeight: '1.5', marginBottom: '25px' }}>
                      You have <strong>0 Premium Credits</strong>. Upgrade your account to unlock full details and apply to elite international companies.
                    </p>
                    <button
                      style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: '#fff', border: 'none', padding: '14px 30px', borderRadius: '8px', fontSize: '1.1rem', fontWeight: 'bold', cursor: 'pointer', width: '100%', boxShadow: '0 4px 6px rgba(245, 158, 11, 0.2)' }}
                      onClick={() => {
                        closeModal();
                        onGoToPayments();
                      }}
                    >
                      Unlock 5 Premium Applies Now
                    </button>
                  </div>
                </>
              ) : (
                <JobPostingPreview formData={selectedJob} />
              )}
            </div>

            {/* Modal Footer & Apply Button */}
            {!isLocked && (
              <div style={{ borderTop: '1px solid #e2e8f0', padding: '16px 32px', background: '#f8fafc', display: 'flex', justifyContent: 'flex-end' }}>
                 {appliedJobIds.has(selectedJob.id || selectedJob._id) ? (
                    <button disabled style={{ padding: '12px 30px', background: '#10b981', color: 'white', border: 'none', borderRadius: '8px', fontSize: '1rem', fontWeight: 'bold', cursor: 'not-allowed' }}>
                        ✔️ Applied Successfully
                    </button>
                 ) : getJobStatus(selectedJob.applyByDate, selectedJob.applyByTime) === 'Closed' ? (
                    <div style={{ color: '#ef4444', fontWeight: 'bold', padding: '12px 0' }}>Application deadline has passed.</div>
                 ) : eligibilityStatus.isEligible ? (
                   <button 
                      style={{ padding: '12px 30px', background: selectedJob.isPremiumJob ? '#1e293b' : '#2563eb', color: 'white', border: 'none', borderRadius: '8px', fontSize: '1rem', fontWeight: 'bold', cursor: 'pointer' }} 
                      onClick={() => handleApplyNow(selectedJob.id || selectedJob._id)}
                   >
                     {selectedJob.isPremiumJob ? `Apply Now (Uses 1 Credit)` : `Apply Now`}
                   </button>
                 ) : (
                   <div style={{ color: '#ef4444', textAlign: 'right' }}>
                     <p style={{ margin: '0 0 5px 0' }}><strong>You may not meet the eligibility criteria:</strong></p>
                     <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '0.9rem', textAlign: 'left', display: 'inline-block' }}>
                        {eligibilityStatus.reasons.map((r, i) => <li key={i}>{r}</li>)}
                     </ul>
                   </div>
                 )}
               </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default CandidateJobs;